import { getConfig } from '../config';
import { AdminApiDomain, getAdminApiUrl } from '../config/ApiRouting';
import type { Competitor } from '../models/ProductTypes';
import { SkinSeoulProductType } from '../models/ProductTypes';

export interface MatchedCompetitorProductRequest {
  sku: string;
  possibility: number;
}

export interface MatchedCompetitorProductsRequest {
  competitor: Competitor;
  competitorProductIdentifiers: MatchedCompetitorProductRequest[];
}

export interface SkinSeoulProductIdentifierRequest {
  sku: string;
  type: SkinSeoulProductType;
  variation?: SkinSeoulProductIdentifierRequest[];
}

export interface MatchWithSkinSeoulProductRequest {
  skinSeoulProduct: SkinSeoulProductIdentifierRequest;
  machineMatchedProducts: MatchedCompetitorProductsRequest[];
  humanMatchedProducts?: MatchedCompetitorProductsRequest[];
  actionedBy?: string;
}

export interface SaveCompetitorMatchRequest {
  skinSeoulSku: string;
  humanMatchedProducts: MatchedCompetitorProductsRequest[];
  machineMatchedProducts: MatchedCompetitorProductsRequest[];
  /** Explicitly undefined when no reviewer is signed in — `exactOptionalPropertyTypes` needs that spelled out. */
  actionedBy?: string | undefined;
}

export interface VerifyNoCompetitorRequest {
  skinSeoulSku: string;
  verified: boolean;
  actionedBy?: string | undefined;
}

export interface MatchCompetitorResponse {
  success: boolean;
}

export interface PriceInfoRequest {
  currency: string;
  originalPrice?: number;
  salesPrice?: number;
}

export interface CompetitorProductRequest {
  productImageUrls: string[];
  sku: string;
  name: string;
  normalizedName: string;
  brand: string;
  category: string[];
  productLink: string;
  size: string;
  pack: string;
  price: PriceInfoRequest[];
}

export interface InsertCompetitorProductRequest {
  competitor: Competitor;
  products: CompetitorProductRequest[];
}

export interface CompetitorProductSearchResult {
  competitor: Competitor;
  products: CompetitorProductRequest[];
}

export interface ProductData {
  skinSeoulProduct: any;
  machineMatchedProducts: Array<{
    competitor: string;
    competitorProducts: any[];
  }>;
}

export interface MatchedSkinSeoulProduct {
  skinSeoulProduct: {
    sku: string;
    name: string;
    brand: string;
    type: 'Simple' | 'Variable' | 'Variation';
    productLink: string;
    productImageUrls: string[];
    category: string[];
    modifiedAt: string;
  };
  machineMatchedProducts: Array<{
    competitor: string;
    competitorProducts: any[];
  }>;
  humanMatchedProducts: Array<{
    competitor: string;
    competitorProducts: any[];
  }>;
  humanDecidedBy?: string;
  humanDecidedAt?: string;
  modifiedAt: string;
}

export interface ListMatchedProductsResponse {
  items: MatchedSkinSeoulProduct[];
  limit: number;
  lastEvaluatedKey?: string | null;
}

export interface FetchExclusiveProductsParams {
  limit?: number;
  lastEvaluatedKey?: string;
  search?: string;
}

export interface FetchMatchedProductsParams {
  limit?: number;
  lastEvaluatedKey?: string;
  actionedBy?: string;
}

export interface UnmatchCompetitorRequest {
  skinSeoulSku: string;
  competitor: Competitor;
  competitorSku?: string;
  actionedBy?: string | undefined;
}

export interface SearchProductsParams {
  q: string;
  page?: number;
  limit?: number;
}

export interface SearchedProduct {
  productId: number;
  sku: string;
  name: string;
  brand: string;
  type: 'Simple' | 'Variable' | 'Variation';
  status: 'instock' | 'outofstock' | 'onbackorder';
  productLink: string;
  productImageUrls: string[];
  category: string[];
}

export interface SearchProductsResponse {
  products: SearchedProduct[];
  total: number;
  page: number;
  limit: number;
}

export interface ListMachineMatchedProductsResponse {
  items: MatchedSkinSeoulProduct[];
  limit?: number;
  lastEvaluatedKey?: string | null;
}

export interface ListMachineUnmatchedProductsResponse {
  items: Array<{
    sku: string;
    name: string;
    brand: string;
    productLink: string;
    productImageUrls: string[];
    category: string[];
    modifiedAt: string;
  }>;
  limit?: number;
  offset?: number;
  total: number;
}

export class CompetitorClient {
  private readonly headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  /** Matching lives on internal-api behind ApiKeyGuard; product insert and search never moved off the facade. */
  private readonly matchBaseUrl = getAdminApiUrl(AdminApiDomain.COMPETITOR);

  private readonly facadeBaseUrl = getConfig().apiUrl;

  private readonly internalHeaders: HeadersInit = { ...this.headers, 'X-API-KEY': getConfig().apiKey };

  public async fetchMachineMatchedProducts(params?: {
    limit?: number;
    lastEvaluatedKey?: string;
    search?: string;
    hasCandidates?: boolean;
    skuLastDigit?: string;
  }): Promise<ListMachineMatchedProductsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params?.lastEvaluatedKey) {
        queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.hasCandidates !== undefined) {
        queryParams.append('hasCandidates', params.hasCandidates.toString());
      }
      if (params?.skuLastDigit) {
        queryParams.append('skuLastDigit', params.skuLastDigit);
      }

      const url = queryParams.toString()
        ? `${this.matchBaseUrl}/competitor/machineMatched?${queryParams.toString()}`
        : `${this.matchBaseUrl}/competitor/machineMatched`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.internalHeaders,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch machine matched products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching machine matched products:', error);
      throw error;
    }
  }

  public async matchCompetitorProduct(request: SaveCompetitorMatchRequest): Promise<MatchCompetitorResponse> {
    const requestBody: MatchWithSkinSeoulProductRequest = {
      skinSeoulProduct: {
        sku: request.skinSeoulSku,
        type: SkinSeoulProductType.Simple,
      },
      machineMatchedProducts: request.machineMatchedProducts || [],
      humanMatchedProducts: request.humanMatchedProducts || [],
      ...(request.actionedBy ? { actionedBy: request.actionedBy } : {}),
    };

    try {
      const response = await fetch(`${this.matchBaseUrl}/competitor/match`, {
        method: 'POST',
        headers: this.internalHeaders,
        mode: 'cors',
        body: JSON.stringify([requestBody]),
      });

      if (!response.ok) {
        throw new Error(`Failed to match competitor product: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error matching competitor product:', error);
      throw error;
    }
  }

  public async insertCompetitorProduct(request: InsertCompetitorProductRequest): Promise<boolean> {
    try {
      const response = await fetch(`${this.facadeBaseUrl}/competitor`, {
        method: 'POST',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to insert competitor product: ${response.statusText}`);
      }

      return true;
    } catch (error) {
      console.error('Error inserting competitor product:', error);
      throw error;
    }
  }

  public async searchCompetitorProducts(competitor: Competitor, term: string): Promise<CompetitorProductSearchResult> {
    try {
      const response = await fetch(
        `${this.matchBaseUrl}/competitor/products/${competitor}/search?term=${encodeURIComponent(term)}`,
        {
          method: 'GET',
          headers: this.internalHeaders,
          mode: 'cors',
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to search competitor products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error searching competitor products:', error);
      throw error;
    }
  }

  public async fetchExclusiveProducts(params: FetchExclusiveProductsParams): Promise<ListMatchedProductsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) {
        queryParams.set('limit', params.limit.toString());
      }
      if (params.lastEvaluatedKey) {
        queryParams.set('lastEvaluatedKey', params.lastEvaluatedKey);
      }
      if (params.search) {
        queryParams.set('search', params.search);
      }

      const response = await fetch(`${this.matchBaseUrl}/competitor/exclusive?${queryParams.toString()}`, {
        method: 'GET',
        headers: this.internalHeaders,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch exclusive products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching exclusive products:', error);
      throw error;
    }
  }

  public async fetchPaginatedMatchedProducts(
    params: FetchMatchedProductsParams,
  ): Promise<ListMatchedProductsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.set('limit', params.limit.toString());
      if (params.lastEvaluatedKey) queryParams.set('lastEvaluatedKey', params.lastEvaluatedKey);
      if (params.actionedBy) {
        queryParams.set('actionedBy', params.actionedBy);
      }

      const response = await fetch(`${this.matchBaseUrl}/competitor/matched?${queryParams.toString()}`, {
        method: 'GET',
        headers: this.internalHeaders,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch matched products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching paginated matched products:', error);
      throw error;
    }
  }

  public async unmatchCompetitor(request: UnmatchCompetitorRequest): Promise<MatchCompetitorResponse> {
    try {
      const { skinSeoulSku, competitor, competitorSku } = request;
      const url = `${this.matchBaseUrl}/competitor/match/${encodeURIComponent(skinSeoulSku)}/${encodeURIComponent(competitor)}/${encodeURIComponent(competitorSku || '')}`;
      const body = { ...(request.actionedBy ? { actionedBy: request.actionedBy } : {}) };

      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.internalHeaders,
        mode: 'cors',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to unmatch competitor: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error unmatching competitor:', error);
      throw error;
    }
  }

  public async searchProducts(params: SearchProductsParams): Promise<SearchProductsResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set('q', params.q);
      if (params.page) queryParams.set('page', params.page.toString());
      if (params.limit) queryParams.set('limit', params.limit.toString());

      const response = await fetch(`${this.facadeBaseUrl}/products/search?${queryParams.toString()}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to search products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  public async getMatchedProductsBySkus(skus: string[]): Promise<MatchedSkinSeoulProduct[]> {
    try {
      const queryParams = new URLSearchParams();
      skus.forEach((sku) => queryParams.append('skus', sku));

      const response = await fetch(`${this.matchBaseUrl}/competitor/matchedProducts?${queryParams.toString()}`, {
        method: 'GET',
        headers: this.internalHeaders,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to get matched products by SKUs: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error getting matched products by SKUs:', error);
      throw error;
    }
  }

  public async verifyNoCompetitor(request: VerifyNoCompetitorRequest): Promise<boolean> {
    try {
      const url = `${this.matchBaseUrl}/competitor/match/${encodeURIComponent(request.skinSeoulSku)}/noCompetitor`;
      const body = { verified: request.verified, ...(request.actionedBy ? { actionedBy: request.actionedBy } : {}) };

      const response = await fetch(url, {
        method: 'PUT',
        headers: this.internalHeaders,
        mode: 'cors',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to confirm no competitor: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error confirming no competitor:', error);
      throw error;
    }
  }

  public async countSkinSeoulProductsSharingCompetitorProduct(competitorSku: string): Promise<number> {
    try {
      const response = await fetch(
        `${this.matchBaseUrl}/competitor/matchedProducts/byCompetitorSku/${encodeURIComponent(competitorSku)}`,
        {
          method: 'GET',
          headers: this.internalHeaders,
          mode: 'cors',
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to count SkinSeoul products sharing a competitor product: ${response.statusText}`);
      }

      const sharingProducts = await response.json();
      return Array.isArray(sharingProducts) ? sharingProducts.length : 0;
    } catch (error) {
      console.error('Error counting SkinSeoul products sharing a competitor product:', error);
      throw error;
    }
  }

  public async fetchMachineUnmatchedProducts(params?: {
    limit?: number;
    offset?: number;
    search?: string;
    skuLastDigit?: string;
  }): Promise<ListMachineUnmatchedProductsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.limit) {
        queryParams.append('limit', params.limit.toString());
      }
      if (params?.offset !== undefined) {
        queryParams.append('offset', params.offset.toString());
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.skuLastDigit) {
        queryParams.append('skuLastDigit', params.skuLastDigit);
      }

      const url = queryParams.toString()
        ? `${this.matchBaseUrl}/competitor/machineUnmatched?${queryParams.toString()}`
        : `${this.matchBaseUrl}/competitor/machineUnmatched`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.internalHeaders,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch machine unmatched products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching machine unmatched products:', error);
      throw error;
    }
  }
}
